import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Header from "./components/Header";
import Index from "./pages/Index";
import EventDetails from "./pages/EventDetails";
import AdminDashboard from "./pages/admin/Dashboard";
import ManageEmployees from "./pages/admin/employees/ManageEmployees";
import EditEmployee from "./pages/admin/employees/EditEmployee";
import EmployeeEvents from "./pages/admin/employees/EmployeeEvents";
import ManagePositionRequests from "./pages/admin/position-requests/ManagePositionRequests";
import CreateEvent from "./pages/admin/events/CreateEvent";
import ManageEvents from "./pages/admin/events/ManageEvents";
import AdminEventDetails from "./pages/admin/events/AdminEventDetails";
import ManagePricing from "./pages/admin/pricing/ManagePricing";
import ManageBids from "./pages/admin/bids/ManageBids";
import BidDetails from "./pages/admin/bids/BidDetails";
import ManageBonuses from "./pages/admin/bonuses/ManageBonuses";
import CreateBonus from "./pages/admin/bonuses/CreateBonus";
import BonusDetails from "./pages/admin/bonuses/BonusDetails";
import EditBonus from "./pages/admin/bonuses/EditBonus";
import EarnedBonusReport from "./pages/admin/bonuses/EarnedBonusReport";
import CreateBonusBatchPage from "./pages/admin/check-batches/CreateBonusBatchPage";
import ManageSeniority from "./pages/admin/seniority/ManageSeniority";
import ManageAnnouncements from "./pages/admin/announcements/ManageAnnouncements";
import ManageClients from "./pages/admin/clients/ManageClients";
import CreateClient from "./pages/admin/clients/CreateClient";
import EditClient from "./pages/admin/clients/EditClient";
import ClientEvents from "./pages/admin/clients/ClientEvents";
import Partners from "./pages/admin/partners/Partners";
import BatchDetails from "./pages/admin/check-batches/[batchId]";
import CreateBatchPage from "./pages/admin/check-batches/CreateBatchPage";
import SignIn from "./pages/auth/SignIn";
import SignUp from "./pages/auth/SignUp";
import RegistrationSuccess from "./pages/RegistrationSuccess";
import Profile from "./pages/Profile";
import BidRequest from "./pages/BidRequest";
import EditPricing from "./pages/admin/pricing/EditPricing";
import EditSeniority from "./pages/admin/seniority/EditSeniority";
import ChangePassword from "./pages/auth/ChangePassword";
import RequestPasswordReset from "./pages/auth/RequestPasswordReset";
import ResetPassword from "./pages/auth/ResetPassword";
import Reports from "./pages/admin/reports/Reports";
import ClientEventReport from "./pages/admin/reports/ClientEventReport";
import UserReport from "./pages/admin/reports/UserReport";
import PaymentCheckReport from "./pages/admin/reports/PaymentCheckReport";
import AuthProvider from "./pages/auth/auth.context";
import { RoleProtectedRoute } from "./components/RoleProtectedRoute";
import { Unauthorized } from "./pages/Unauthorized";
import { ProtectedRoute } from "./components/ProtectedRoute";
import ProtectedLayout from "./pages/auth/ProtectedLayout";
import AdminLayout from "./pages/auth/AdminProtectedLayout";
import Layout from "./components/Layout";
import NotFound from "./pages/NotFound";
import ManageSettings from "./pages/admin/settings/ManageSettings";
import ManageFullDates from "./pages/admin/full-dates/ManageFullDates";
import ManageEmailTemplates from "./pages/admin/email/ManageEmailTemplates";
import EmployeeCertifications from "./pages/EmployeeCertifications";
import ManageNotificationLogs from "./pages/admin/notifications/ManageNotificationLogs";
import VCardDownloadPage from "./pages/vcard/VCardDownloadPage";
import SentryTest from "./components/admin/sentry/SentryTest";
import BidPrintView from "./pages/admin/bids/BidPrintView";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import DocumentLibrary from "./pages/admin/documents/DocumentLibrary";
import AssignmentManagement from "./pages/admin/assignments/AssignmentManagement";
import UserDocumentDashboard from "./pages/documents/UserDocumentDashboard";
import EventDetailsPrintPage from "./pages/admin/events/EventDetailsPrintPage";
import JournalFeed from "./pages/admin/journal/JournalFeed";
import EventJournal from "./pages/admin/journal/EventJournal";
import BidRequestSuccess from "./pages/BidRequestSuccess";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

const App = () => (
  <AuthProvider>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter basename="/">
        <TooltipProvider>
          <Layout>
            <Routes>
              {/* Public Routes */}
              <Route path="/change-password" element={<ChangePassword />} />
              <Route
                path="/request-password-reset"
                element={<RequestPasswordReset />}
              />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/unauthorized" element={<Unauthorized />} />
              <Route path="/signin" element={<SignIn />} />
              <Route path="/signup" element={<SignUp />} />
              <Route
                path="/registration-success"
                element={<RegistrationSuccess />}
              />
              <Route path="/bid-request" element={<BidRequest />} />
              <Route
                path="/vcard/download/:id"
                element={<VCardDownloadPage />}
              />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route
                path="/bid-request-success"
                element={<BidRequestSuccess />}
              />

              {/* Protected Routes */}
              <Route element={<ProtectedLayout />}>
                <Route path="/" element={<Index />} />
                <Route path="/documents" element={<UserDocumentDashboard />} />
                <Route path="/event/:id" element={<EventDetails />} />
                <Route path="/employee-info/*" element={<Profile />} />
                <Route
                  path="/employee-certifications"
                  element={<EmployeeCertifications />}
                />
              </Route>

              {/* Admin Routes (Requires ROLE_ADMIN) */}
              <Route element={<AdminLayout />}>
                <Route path="/admin/dashboard" element={<AdminDashboard />} />
                <Route path="/admin/documents" element={<DocumentLibrary />} />
                <Route
                  path="/admin/assignments"
                  element={<AssignmentManagement />}
                />
                <Route path="/admin/reports" element={<Reports />} />
                <Route
                  path="/admin/reports/client-events"
                  element={<ClientEventReport />}
                />
                <Route
                  path="/admin/reports/user-report"
                  element={<UserReport />}
                />
                <Route
                  path="/admin/reports/payment-checks"
                  element={<PaymentCheckReport />}
                />
                <Route path="/admin/employees" element={<ManageEmployees />} />
                <Route
                  path="/admin/employees/:id/edit"
                  element={<EditEmployee />}
                />
                <Route
                  path="/admin/employees/:id/events/:timeframe"
                  element={<EmployeeEvents />}
                />
                <Route
                  path="/admin/position-requests"
                  element={<ManagePositionRequests />}
                />
                <Route path="/admin/events/create" element={<CreateEvent />} />
                <Route
                  path="/admin/events/:id"
                  element={<AdminEventDetails />}
                />
                <Route path="/admin/events" element={<ManageEvents />} />
                <Route path="/admin/pricing" element={<ManagePricing />} />
                <Route
                  path="/admin/pricing/:id/edit"
                  element={<EditPricing />}
                />
                <Route path="/admin/pricing/:id" element={<EditPricing />} />
                <Route path="/admin/bids" element={<ManageBids />} />
                <Route path="/admin/bids/:id" element={<BidDetails />} />
                <Route
                  path="/admin/bids/:id/print"
                  element={<BidPrintView />}
                />
                <Route path="/admin/bonuses" element={<ManageBonuses />} />
                <Route path="/admin/bonuses/create" element={<CreateBonus />} />
                <Route path="/admin/bonuses/:id" element={<BonusDetails />} />
                <Route path="/admin/bonuses/:id/edit" element={<EditBonus />} />
                <Route
                  path="/admin/earned-bonuses"
                  element={<EarnedBonusReport />}
                />
                <Route
                  path="/admin/check-batches/create-bonus"
                  element={<CreateBonusBatchPage />}
                />
                <Route path="/admin/seniority" element={<ManageSeniority />} />
                <Route
                  path="/admin/seniority/:id/edit"
                  element={<EditSeniority />}
                />
                <Route path="/admin/clients" element={<ManageClients />} />
                <Route
                  path="/admin/clients/create"
                  element={<CreateClient />}
                />
                <Route
                  path="/admin/clients/:id/edit"
                  element={<EditClient />}
                />
                <Route
                  path="/admin/clients/:id/events"
                  element={<ClientEvents />}
                />
                <Route
                  path="/admin/announcements"
                  element={<ManageAnnouncements />}
                />
                <Route path="/admin/settings" element={<ManageSettings />} />
                <Route path="/admin/full-dates" element={<ManageFullDates />} />
                <Route
                  path="/admin/email-templates"
                  element={<ManageEmailTemplates />}
                />
                <Route
                  path="/admin/notification-logs"
                  element={<ManageNotificationLogs />}
                />
                <Route path="/admin/sentry-test" element={<SentryTest />} />
                <Route path="/admin/partners" element={<Partners />} />
                <Route
                  path="/admin/check-batches/create/:id"
                  element={<CreateBatchPage />}
                />
                <Route
                  path="/admin/check-batches/:batchId"
                  element={<BatchDetails />}
                />
                <Route
                  path="/admin/events/:id/print"
                  element={<EventDetailsPrintPage />}
                />
                <Route
                  path="/admin/events/:id/journal"
                  element={<EventJournal />}
                />
                <Route path="/admin/journal" element={<JournalFeed />} />
              </Route>

              {/* 404 Route - Must be last */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Layout>
          <Toaster />
          <Sonner />
        </TooltipProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </AuthProvider>
);

export default App;
